import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { buildCaregiverProfileLink } from '@/lib/share';
import { getBrowserTimeZone } from '@/lib/time-zones';
import { useAuthStore } from '@/store/auth-store';
import { useOnboardingStore } from '@/store/onboarding-store';
import { useToast } from '@/hooks/use-toast';
import type { PersonalInfoData, ServicesData } from '@/types/profile';
import type { User } from '@/types/user';

interface UseOnboardingOptions {
  totalSteps: number;
  steps: { step: number; path: string }[];
  initialData?: Record<string, unknown>;
}

interface OnboardingData {
  personalInfo?: PersonalInfoData;
  services?: ServicesData;
  profileLink?: string;
  linkCode?: string;
}

interface OnboardingState {
  completedSteps: Set<number>;
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;
}

export function useOnboarding({ totalSteps, steps, initialData = {} }: UseOnboardingOptions) {
  const navigate = useNavigate();
  const { user, updateUser, setProfileComplete } = useAuthStore();
  const { success: showSuccess, error: showError } = useToast();

  // Use the persisted onboarding store
  const {
    data: persistedData,
    setPersonalInfo,
    setServices,
    pendingProfilePhotoFile,
    clearPendingProfilePhotoFile,
    setProfileLink,
    markStepComplete,
    clearOnboarding,
  } = useOnboardingStore();

  // Derive onboarding data from initialData + persisted store on every render.
  // This avoids stale copies inside local React state.
  const onboardingData: OnboardingData = {
    ...(initialData as OnboardingData),
    ...persistedData,
  };

  const [state, setState] = useState<OnboardingState>({
    completedSteps: new Set(),
    isLoading: false,
    isInitializing: true,
    error: null,
  });

  // Initialize onboarding state based on user profile completion
  useEffect(() => {
    if (user) {
      const completedSteps = new Set<number>();

      // Step 1 completion can be inferred from backend user profile
      if (user.firstName && user.lastName && user.phoneNumber) {
        completedSteps.add(1);
      }

      setState(prev => ({
        ...prev,
        completedSteps,
      }));

      if (user.userRole !== 'CARE_GIVER') {
        setState(prev => ({ ...prev, isInitializing: false }));
        return;
      }

      // Fetch caregiver profile to check if it exists and get profile link
      const fetchProfile = async () => {
        try {
          const profile = await api.get('/caregiver/profile');

          console.log('Fetched existing profile:', profile);

          if (profile && (profile as { profileLinkPath?: string }).profileLinkPath) {
            const linkCode = (profile as { profileLinkPath: string }).profileLinkPath;
            const profileLink = buildCaregiverProfileLink(linkCode);

            // Persist to store
            setProfileLink(linkCode, profileLink);
            markStepComplete(2);
            markStepComplete(3);

            setState(prev => ({
              ...prev,
              completedSteps: new Set([...prev.completedSteps, 2, 3]),
              isInitializing: false,
            }));
            setProfileComplete(true);
          } else {
            setState(prev => ({ ...prev, isInitializing: false }));
          }
        } catch {
          // Profile doesn't exist yet - that's okay, user will create it in step 2
          console.log('No caregiver profile found yet');
          setState(prev => ({ ...prev, isInitializing: false }));
        }
      };

      fetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only run when user changes

  // Get current step from URL
  const getCurrentStep = useCallback(() => {
    const currentPath = window.location.pathname;
    const currentStepData = steps.find(step => step.path === currentPath);
    return currentStepData?.step || 1;
  }, [steps]);

  // Check if a step can be accessed (not skipping ahead)
  const canAccessStep = useCallback(
    (targetStep: number) => {
      if (targetStep === 1) return true;

      // Can access if previous step is completed
      return state.completedSteps.has(targetStep - 1);
    },
    [state.completedSteps]
  );

  // Navigate to a specific step with validation
  const goToStep = useCallback(
    (targetStep: number) => {
      if (!canAccessStep(targetStep)) {
        console.warn(`Cannot skip to step ${targetStep}. Complete previous steps first.`);
        return false;
      }

      const stepData = steps.find(step => step.step === targetStep);
      if (stepData) {
        navigate(stepData.path);
        return true;
      }
      return false;
    },
    [canAccessStep, navigate, steps]
  );

  // Go to next step
  const goToNextStep = useCallback(() => {
    const currentStep = getCurrentStep();
    if (currentStep < totalSteps) {
      return goToStep(currentStep + 1);
    }
    return false;
  }, [getCurrentStep, goToStep, totalSteps]);

  // Go to previous step
  const goToPreviousStep = useCallback(() => {
    const currentStep = getCurrentStep();
    if (currentStep > 1) {
      return goToStep(currentStep - 1);
    }
    return false;
  }, [getCurrentStep, goToStep]);

  // Save personal info (Step 1)
  const savePersonalInfo = useCallback(
    async (data: PersonalInfoData) => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const timeZone = data.timeZone || user?.timeZone || getBrowserTimeZone();
        const updatePayload: Partial<User> = {
          firstName: data.firstName,
          lastName: data.lastName,
          phoneNumber: data.phoneNumber,
          timeZone,
        };

        if (data.notificationPrefs !== undefined) {
          updatePayload.notificationPrefs = data.notificationPrefs;
        }

        const updatedUser = await api.patch<Partial<User>>('/api/v1/users/me', updatePayload);

        // Update user in auth store
        updateUser(updatedUser);

        // Persist to onboarding store
        setPersonalInfo({ ...data, timeZone });
        markStepComplete(1);

        // Update only step tracking + loading state locally
        setState(prev => ({
          ...prev,
          completedSteps: new Set([...prev.completedSteps, 1]),
          isLoading: false,
        }));

        showSuccess('Personal information saved', {
          description: 'Your information has been saved successfully.',
        });

        return true;
      } catch (err) {
        console.error('Failed to save personal info:', err);
        // Error messages are already user-friendly from the API client
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to save your information. Please try again.';

        setState(prev => ({ ...prev, isLoading: false, error: errorMessage }));
        showError('Error', { description: errorMessage });
        return false;
      }
    },
    [markStepComplete, setPersonalInfo, showError, showSuccess, updateUser, user?.timeZone]
  );

  // Save services info (Step 2) - Create caregiver profile
  const saveServices = useCallback(
    async (data: ServicesData) => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        // Map frontend data to backend format (camelCase for API)
        const profileData = {
          bookingAvailability: 'AVAILABLE' as const,
          profileLinkAccess: 'PUBLIC' as const,
          aboutMe: data.aboutMe,
          serviceAreas: data.serviceAreas,
          rolesForHire: data.serviceTypes,
          ageRangesServed: data.ageRangesNotApplicable ? [] : data.ageRanges,
        };

        const response = await api.post<{ profileLinkPath: string }>(
          '/caregiver/profile',
          profileData
        );

        console.log('Profile creation response:', response);

        const linkCode = response.profileLinkPath;
        const profileLink = buildCaregiverProfileLink(linkCode);

        if (pendingProfilePhotoFile) {
          try {
            const formData = new FormData();
            formData.append('file', pendingProfilePhotoFile);
            await api.request('/api/v1/me/care-giver-profile/photo', {
              method: 'POST',
              body: formData,
            });
          } catch (photoError) {
            console.warn('Profile photo upload after profile creation failed.', photoError);
            showError('Profile photo not uploaded', {
              description:
                'Your profile was created successfully. You can add your photo later in Profile Settings.',
            });
          } finally {
            clearPendingProfilePhotoFile();
          }
        }

        // Persist to onboarding store (source of truth)
        setServices(data);
        setProfileLink(linkCode, profileLink);
        markStepComplete(2);
        markStepComplete(3);
        clearOnboarding();

        // Update only step tracking + loading state locally
        setState(prev => ({
          ...prev,
          completedSteps: new Set([...prev.completedSteps, 2, 3]), // Complete both steps 2 and 3
          isLoading: false,
        }));

        // Mark profile as complete
        setProfileComplete(true);

        showSuccess('Profile created successfully!', {
          description: 'Your caregiver profile is now ready to share.',
        });

        return true;
      } catch (error) {
        console.error('Failed to save services:', error);

        let errorMessage = 'Failed to save your service information. Please try again.';

        // Handle specific error cases
        if (error instanceof Error) {
          if (error.message.includes('duplicate') || error.message.includes('already exists')) {
            errorMessage =
              'A profile already exists. Please try refreshing the page or contact support.';
          } else {
            errorMessage = error.message;
          }
        }

        setState(prev => ({ ...prev, isLoading: false, error: errorMessage }));
        showError('Profile Creation Error', { description: errorMessage });
        return false;
      }
    },
    [
      pendingProfilePhotoFile,
      clearPendingProfilePhotoFile,
      setServices,
      setProfileLink,
      markStepComplete,
      clearOnboarding,
      setProfileComplete,
      showSuccess,
      showError,
    ]
  );

  // Complete onboarding (Step 3)
  const completeOnboarding = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await api.post<{
        profileId: string;
        linkCode: string;
        profileUrl: string;
      }>('/api/caregivers/onboarding/complete');

      // Mark profile as complete
      setProfileComplete(true);

      // Persist completion output into store so UI/state is consistent
      setProfileLink(response.linkCode, response.profileUrl);

      // Update only step tracking + loading state locally
      setState(prev => ({
        ...prev,
        completedSteps: new Set([...prev.completedSteps, 3]),
        isLoading: false,
      }));

      showSuccess('Profile created successfully!', {
        description: 'Your caregiver profile is now ready to share.',
      });

      return {
        profileId: response.profileId,
        linkCode: response.linkCode,
        profileUrl: response.profileUrl,
      };
    } catch (error) {
      console.error('Failed to complete onboarding:', error);

      let errorMessage = 'Failed to complete your profile setup. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      setState(prev => ({ ...prev, isLoading: false, error: errorMessage }));
      showError('Error', { description: errorMessage });
      return null;
    }
  }, [setProfileComplete, setProfileLink, showSuccess, showError]);

  // Mark current step as complete with data
  const completeCurrentStep = useCallback(
    (stepData?: Record<string, unknown>) => {
      const currentStep = getCurrentStep();

      // Persist arbitrary step data into store (source of truth)
      if (stepData) {
        useOnboardingStore.setState(prev => ({
          data: { ...prev.data, ...(stepData as OnboardingData) },
        }));
      }

      setState(prev => ({
        ...prev,
        completedSteps: new Set([...prev.completedSteps, currentStep]),
      }));
    },
    [getCurrentStep]
  );

  // Update data for current step without completing it
  const updateStepData = useCallback((stepData: Record<string, unknown>) => {
    // Persist arbitrary step data into store (source of truth)
    useOnboardingStore.setState(prev => ({
      data: { ...prev.data, ...(stepData as OnboardingData) },
    }));
  }, []);

  // Check if onboarding is complete
  const isOnboardingComplete = useCallback(() => {
    // Onboarding is complete if all steps are done and we have a profile link
    const isComplete = state.completedSteps.size === totalSteps && !!onboardingData.profileLink;
    console.log('isOnboardingComplete check:', {
      completedStepsSize: state.completedSteps.size,
      totalSteps,
      profileLink: onboardingData.profileLink,
      completedSteps: Array.from(state.completedSteps),
      isComplete,
    });
    return isComplete;
  }, [state.completedSteps, totalSteps, onboardingData.profileLink]);

  // Get data for a specific step or all data
  const getStepData = useCallback(
    (step?: number) => {
      if (step === 1) {
        return onboardingData.personalInfo || {};
      }
      if (step === 2) {
        return onboardingData.services || {};
      }
      if (step === 3) {
        return {
          linkCode: onboardingData.linkCode,
          profileLink: onboardingData.profileLink,
        };
      }
      return onboardingData;
    },
    [onboardingData]
  );

  // Check profile completion status (synchronous version)
  const checkProfileCompletion = useCallback(() => {
    if (!user) return { isComplete: false, nextStep: 1 };

    // Check if email is verified
    if (!user.isVerified) {
      return { isComplete: false, nextStep: 0, requiresEmailVerification: true };
    }

    // Check personal info
    if (!user.firstName || !user.lastName || !user.phoneNumber) {
      return { isComplete: false, nextStep: 1 };
    }

    // For now, assume profile needs to be created if we reach here
    // In a real implementation, we'd check the profile completion status
    return { isComplete: false, nextStep: 2 };
  }, [user]);

  // Navigate to appropriate step based on completion status
  const navigateToAppropriateStep = useCallback(() => {
    const { isComplete, nextStep, requiresEmailVerification } = checkProfileCompletion();

    if (requiresEmailVerification) {
      navigate('/auth/verify-email');
      return;
    }

    if (isComplete) {
      navigate('/dashboard');
      return;
    }

    if (nextStep !== null) {
      const stepData = steps.find(step => step.step === nextStep);
      if (stepData) {
        navigate(stepData.path);
      }
    }
  }, [checkProfileCompletion, navigate, steps]);

  return {
    // State
    currentStep: getCurrentStep(),
    totalSteps,
    onboardingData,
    completedSteps: Array.from(state.completedSteps),
    isLoading: state.isLoading,
    isInitializing: state.isInitializing,
    error: state.error,

    // Navigation
    canAccessStep,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    navigateToAppropriateStep,

    // Data management
    completeCurrentStep,
    updateStepData,
    getStepData,

    // API actions
    savePersonalInfo,
    saveServices,
    completeOnboarding,

    // Status checks
    isOnboardingComplete,
    checkProfileCompletion,
  };
}
